import React, { useState, useEffect, useRef } from 'react';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';

interface LoginProps {
    onRegisterToggle?: () => void;
}

const Login: React.FC<LoginProps> = ({ onRegisterToggle }) => {
    const { login, isLoading, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const hasAttemptedLogin = useRef(false);

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        rememberMe: false
    });

    // Redirect to profile page after successful authentication
    useEffect(() => {
        if (isAuthenticated && hasAttemptedLogin.current) {
            navigate('/profile');
        }
    }, [isAuthenticated, navigate]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
        
        // Clear login error when user starts typing
        const errorContainer = document.getElementById('login-error-container');
        if (errorContainer && !errorContainer.classList.contains('hidden')) {
            errorContainer.classList.add('hidden');
        }
    };
    // a
    const validateForm = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.email.trim()) {
            newErrors.email = 'Имейлът е задължителен';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Невалиден имейл адрес';
        }

        if (!formData.password) {
            newErrors.password = 'Паролата е задължителна';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        hasAttemptedLogin.current = true;
        
        // Hide error before attempting login
        const errorContainer = document.getElementById('login-error-container');
        if (errorContainer) {
            errorContainer.classList.add('hidden');
        }
        
        try {
            await login(formData.email, formData.password);
            // Clear any previous errors on successful login
            setErrors({});
        } catch (error: any) {
            // Show error using direct DOM manipulation
            setTimeout(() => {
                const errorContainer = document.getElementById('login-error-container');
                if (errorContainer) {
                    errorContainer.classList.remove('hidden');
                }
            }, 100);
        }
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 py-6 px-4 sm:py-12 sm:px-6 lg:px-8">
            <div className="w-full max-w-sm sm:max-w-md space-y-6 sm:space-y-8">
                {/* Лого */}
                <div className="text-center">
                    <div className="flex items-center justify-center mb-8 sm:mb-10">
                        <Link to='/'>
                            <img
                                src={require('../logo.png')}
                                alt="PCCenter Logo"
                                className="h-14 sm:h-16 w-auto mx-auto transition-transform duration-200 hover:scale-110 object-contain"
                            />
                        </Link>
                    </div>
                </div>

                {/* Заглавие */}
                <div className="text-center px-2">
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">
                        Влезте в акаунта си
                    </h2>
                    <p className="mt-3 text-sm sm:text-base text-gray-600">
                        Или{' '}
                        <button
                            onClick={onRegisterToggle}
                            className="font-medium text-[#31B823] hover:text-[#28A01D] underline underline-offset-2"
                        >
                            създайте нов акаунт
                        </button>
                    </p>
                </div>

                <form className="space-y-6" onSubmit={handleSubmit}>
                    <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 shadow-sm">
                        <div className="space-y-4 sm:space-y-5">
                            {/* Имейл */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Имейл адрес
                                </label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className={`w-full pl-10 pr-3 py-3 sm:py-2.5 border rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#31B823] transition-colors ${
                                            errors.email ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="example@email.com"
                                        autoComplete="email"
                                    />
                                </div>
                                {errors.email && (
                                    <p className="text-red-500 text-xs mt-1.5">{errors.email}</p>
                                )}
                            </div>

                            {/* Парола */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Парола
                                </label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                        name="password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        className={`w-full pl-10 pr-12 py-3 sm:py-2.5 border rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#31B823] transition-colors ${
                                            errors.password ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="Въведете парола"
                                        autoComplete="current-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 touch-manipulation"
                                    >
                                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                                {errors.password && (
                                    <p className="text-red-500 text-xs mt-1.5">{errors.password}</p>
                                )}
                            </div>

                            {/* Запомни ме и забравена парола */}
                            <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
                                <div className="flex items-center">
                                    <input
                                        name="rememberMe"
                                        type="checkbox"
                                        checked={formData.rememberMe}
                                        onChange={handleInputChange}
                                        className="h-4 w-4 text-[#31B823] border-gray-300 rounded focus:ring-[#31B823] focus:ring-2"
                                    />
                                    <label className="ml-2 text-sm text-gray-700 select-none">
                                        Запомни ме
                                    </label>
                                </div>

                                <div className="text-sm">
                                    <Link
                                        to="/forgot-password"
                                        className="font-medium text-[#31B823] hover:text-[#28A01D] underline underline-offset-2"
                                    >
                                        Забравена парола?
                                    </Link>
                                </div>
                            </div>

                            {/* Грешка при изпращане */}
                            <div id="login-error-container" className="hidden bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                                <span id="login-error-text">Грешен e-mail или парола</span>
                            </div>

                            {/* Бутон за влизане */}
                            <div className="pt-2">
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full bg-[#31B823] hover:bg-[#28A01D] disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-3.5 sm:py-3 px-4 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#31B823] focus:ring-offset-2 transition-colors touch-manipulation"
                                >
                                    {isLoading ? (
                                        <div className="flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                            Влизане...
                                        </div>
                                    ) : (
                                        'Влез'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Login;